import type { Command } from "commander";
import { AiModelsService } from "../../models/AiModelsService.js";
import { resolveCatalogCacheTtlMs } from "../../cache/modelCache.js";

function service(opts: { bundledOnly?: boolean }) {
  return new AiModelsService({
    cacheTtlMs: resolveCatalogCacheTtlMs(),
    bundledOnly: opts.bundledOnly,
  });
}

export function registerModelsCommand(program: Command): void {
  const models = program.command("models").description("Inspect model catalogs");

  models
    .command("list")
    .description("List models with optional filters")
    .option("--provider <id>", "Filter by providerId (e.g. openai, anthropic)")
    .option("--output-modality <m>", "Filter by output modality (text, image, audio, …)")
    .option("--input-modality <m>", "Filter by input modality")
    .option("--parameter <p>", "Filter by supported parameter (e.g. tools)")
    .option("--tools", "Only models that support tools")
    .option("--reasoning", "Only reasoning / thinking models")
    .option("--search <q>", "Search name, id, description")
    .option("--json", "Output raw JSON")
    .option("--limit <n>", "Max rows", "50")
    .option("--offset <n>", "Skip rows", "0")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (opts: {
      provider?: string;
      outputModality?: string;
      inputModality?: string;
      parameter?: string;
      tools?: boolean;
      reasoning?: boolean;
      search?: string;
      json?: boolean;
      limit?: string;
      offset?: string;
      bundledOnly?: boolean;
    }) => {
      const { models: rows, total } = await service(opts).listModels({
        providerId: opts.provider,
        outputModality: opts.outputModality,
        inputModality: opts.inputModality,
        supportedParameter: opts.parameter,
        supportsTools: opts.tools ? true : undefined,
        supportsReasoning: opts.reasoning ? true : undefined,
        search: opts.search,
        limit: Number.parseInt(opts.limit ?? "50", 10),
        offset: Number.parseInt(opts.offset ?? "0", 10),
      });

      if (opts.json) {
        console.log(JSON.stringify({ total, models: rows }, null, 2));
        return;
      }

      console.log(
        "MODEL ID".padEnd(42),
        "PROVIDER".padEnd(14),
        "OUTPUT".padEnd(10),
        "R".padEnd(3),
        "NAME",
      );
      console.log("─".repeat(104));
      for (const m of rows) {
        console.log(
          m.modelId.padEnd(42),
          m.providerId.padEnd(14),
          m.primaryOutputModality.padEnd(10),
          (m.supportsReasoning ? "yes" : "no").padEnd(3),
          m.name.slice(0, 40),
        );
      }
      console.log(`\n${rows.length} shown · ${total} matching`);
    });

  models
    .command("get")
    .description("Get full model record by ID or alias")
    .argument("<modelId>", "Model ID or alias")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (modelId: string, opts: { bundledOnly?: boolean }) => {
      const model = await service(opts).getModelInfo(modelId);
      if (!model) {
        console.error(`Model not found: ${modelId}`);
        process.exit(1);
      }
      console.log(JSON.stringify(model, null, 2));
    });

  models
    .command("resolve")
    .description("Resolve provider + model to canonical id")
    .requiredOption("--model <m>", "Model string (required)")
    .option("--provider <p>", "Provider hint (openrouter, openai, …)")
    .option("--threshold <n>", "Confidence threshold 0–1", "0.6")
    .option("--json", "Output raw ModelResolutionResult JSON")
    .option("--verbose", "Show strategy trail")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (opts: {
      model: string;
      provider?: string;
      threshold?: string;
      json?: boolean;
      verbose?: boolean;
      bundledOnly?: boolean;
    }) => {
      const result = await service(opts).resolve(opts.model, opts.provider);

      if (opts.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }

      const providerLabel = opts.provider ?? "unspecified";
      console.log(`Input:     provider="${providerLabel}"  model="${opts.model}"`);

      if (opts.verbose && result.found) {
        console.log(`Normalised: model="${result.normalisedInput}"`);
        console.log("\nStrategy trail:");
        for (const s of result.resolvedVia) {
          console.log(`  • ${s}`);
        }
        console.log();
      }

      if (!result.found) {
        console.log("Result:    NOT FOUND");
        console.log(`Reason:    ${result.reason}`);
        if (result.bestRejectedCandidate) {
          console.log(
            `Candidate: ${result.bestRejectedCandidate.modelId} (confidence ${result.bestRejectedCandidate.confidence.toFixed(2)})`,
          );
        }
        console.log('Tip:       Run "ai-tools catalog refresh" or pass a profile/choice key / concrete model id.');
        process.exit(1);
        return;
      }

      console.log(`Resolved:  ${result.modelId}`);
      console.log(`Name:      ${result.record?.name ?? "(no catalog record)"}`);
      console.log(`Strategy:  ${result.resolvedVia.join(" → ")}`);
      console.log(`Confidence: ${result.confidence.toFixed(2)}`);
      console.log(`Via OR:    ${result.routedViaOpenRouter ? "yes" : "no"}`);
    });

  models
    .command("vendor")
    .description("Find model vendor from model name alone (best-effort)")
    .requiredOption("--model <m>", "Model string (required)")
    .option("--openrouter", "Return OpenRouter transport shape (provider openrouter, model vendor/slug)")
    .option("--json", "Output raw JSON")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (opts: {
      model: string;
      openrouter?: boolean;
      json?: boolean;
      bundledOnly?: boolean;
    }) => {
      const ref = await service(opts).resolveVendor(opts.model, {
        asOpenRouter: opts.openrouter,
      });

      if (opts.json) {
        console.log(JSON.stringify(ref, null, 2));
        if (!ref) process.exit(1);
        return;
      }

      console.log(`Input:  model="${opts.model}"`);
      if (!ref) {
        console.log("Result: NOT FOUND");
        process.exit(1);
        return;
      }

      console.log(`Vendor:   ${ref.provider}`);
      console.log(`Model:    ${ref.model}`);
    });

  models
    .command("count")
    .description("Count models matching filters")
    .option("--provider <id>", "Filter by providerId")
    .option("--output-modality <m>", "Filter by output modality")
    .option("--parameter <p>", "Filter by supported parameter")
    .option("--tools", "Only models that support tools")
    .option("--reasoning", "Only reasoning / thinking models")
    .option("--bundled-only", "Use src/data catalogs only (no HTTP)")
    .action(async (opts: {
      provider?: string;
      outputModality?: string;
      parameter?: string;
      tools?: boolean;
      reasoning?: boolean;
      bundledOnly?: boolean;
    }) => {
      const n = await service(opts).countModels({
        providerId: opts.provider,
        outputModality: opts.outputModality,
        supportedParameter: opts.parameter,
        supportsTools: opts.tools ? true : undefined,
        supportsReasoning: opts.reasoning ? true : undefined,
      });
      console.log(n);
    });
}
